<template>
  <base-drawer :show="show" title="Attributed purchases" width="640px" @close="$emit('close')">
    <drawer-section title="Purchases" icon="icon-money-coins">
      <template #header-actions>
        <base-button v-if="canDownload" icon class="btn-link text-white m-0" :disabled="exporting" title="Download CSV" @click.stop="$emit('download')">
          <i :class="exporting ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'"></i>
        </base-button>
      </template>
      <p class="mb-3"><small class="text-white">Active orders placed within 7 days of a recipient's first click on this campaign.</small></p>
      <data-table
        class="data-table--compact"
        :columns="columns"
        :rows="rows"
        row-key="transaction_id"
        :loading="loading"
        :page="page"
        :per-page="perPage"
        :total="total"
        @update:page="$emit('update:page', $event)"
      >
        <template #cell(customer)="{ row }">
          <span class="text-white">{{ customerName(row) }}</span>
        </template>
        <template #cell(order_number)="{ row }">
          <a
            v-if="row.order_number"
            class="text-primary"
            :href="`/purchases/transactions/details?order_number=${row.order_number}`"
            target="_blank"
            rel="noopener"
          >#{{ row.order_number }}&nbsp;<i class="tim-icons el-icon-top-right" style="font-size: 10px;"></i></a>
          <span v-else class="text-white">-</span>
        </template>
        <template #cell(timestamp)="{ row }">
          <span class="text-white">{{ formatDate(row.timestamp) }}</span>
          <div class="data-table__muted">Clicked {{ formatDate(row.first_click) }}</div>
        </template>
        <template #cell(paid)="{ row }">
          <span class="text-white">{{ formatMoney(row.paid) }}</span>
          <div v-if="row.refunded > 0" class="data-table__muted">Refunded {{ formatMoney(row.refunded) }}</div>
        </template>
        <template #empty>
          <p class="data-table__empty-text">No purchases yet</p>
        </template>
      </data-table>
    </drawer-section>
  </base-drawer>
</template>

<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'
import { formatGroupDate } from '@/plugins/dates'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'

const props = defineProps({
  show: { type: Boolean, default: false },
  rows: { type: Array as () => any[], default: () => [] },
  loading: { type: Boolean, default: false },
  page: { type: Number, default: 1 },
  perPage: { type: Number, default: 25 },
  total: { type: Number, default: 0 },
  canDownload: { type: Boolean, default: false },
  exporting: { type: Boolean, default: false }
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:page', page: number): void
  (e: 'download'): void
}>()

const columns = [
  { key: 'customer', label: 'Customer' },
  { key: 'order_number', label: 'Order' },
  { key: 'timestamp', label: 'Purchased' },
  { key: 'paid', label: 'Paid', align: 'right' as const }
]

function customerName(row: any) {
  return `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Customer'
}

// Group date format, including the show day of week setting (formatGroupDate default)
function formatDate(value: string) {
  return value ? formatGroupDate(value) : ''
}

function formatMoney(value: number) {
  const gs = getGroupSettings()
  return formatCurrency(value, {
    currency: gs.currency,
    currencyFormat: gs.currency_format,
    numberFormat: gs.number_format
  })
}

// BaseDrawer does not handle ESC; each consumer adds its own listener
function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close')
}
watch(() => props.show, (val) => {
  if (val) document.addEventListener('keydown', onEscKey)
  else document.removeEventListener('keydown', onEscKey)
})
onBeforeUnmount(() => document.removeEventListener('keydown', onEscKey))
</script>

<script lang="ts">
export default { name: 'attributed-purchases-drawer' }
</script>
